import {btn, comp, Component, h4, List, list, searchbtn, SearchButton, Store, t, tbar} from "@intermesh/goui";
import {Board, boardDS, Card, cardDS, projectDS, taskDS, ticketDS} from "./Index";
import {AclLevel, client} from "@intermesh/groupoffice-core";
import {cardComp} from "./CardComponent.js";
import {BoardFilterDialog} from "./BoardFilterDialog.js";

export class BacklogColumn extends Component {
	public cardGrid: List;
	private board!: Board;
	private readonly collapsedComp: Component;
	private readonly expandedComp: Component;
	private searchButton: SearchButton;
	private loaded: boolean;

	constructor() {
		super();

		this.cls = "bg-lowest";
		this.style = {padding: ".8rem 0"}
		this.flex = 1;

		this.loaded = false;

		this.cardGrid = list({
			store: new Store(),
			renderer: (record) => {
				if (record.entityData) {
					return [cardComp({record: record})];
				} else {
					return "";
				}
			},
			cls: "scroll kanban-card-grid",
			flex: 1,
			sortableGroup: "kanban-card",
			emptyStateHtml: "",
			dropOn: false,
			dropBetween: true,
			rowSelectionConfig: {
				multiSelect: false
			},
			listeners: {
				drop: async ({dragDataSet}) => {
					const draggedCard = dragDataSet.selectedRowIndexes[0].record;

					if (draggedCard.id) {
						await cardDS.destroy(draggedCard.id);
						void this.load(this.board.id)
					}
				},
				rowdblclick: async ({storeIndex}) => {
					const record = this.cardGrid.store.get(storeIndex)!;

					const win = new go.links.LinkDetailWindow({
						entity: record.entity
					});

					win.load(record.entityId);
				}
			}
		});

		this.stateId = "kanban-backlog-column"

		this.expandedComp = comp({
				cls: "vbox kanban-backlog",
				hidden: false
			},
			tbar({cls: "border-bottom bg-mid", style: {borderTopRightRadius: "0.8rem"}},
				h4({
					cls: "kanban-column-title",
					text: t("Backlog")
				}),
				"->",
				btn({
					icon: "filter_alt",
					title: t("Set filters to board"),
					handler: async () => {
						const dlg = new BoardFilterDialog();

						dlg.form.on("submit", () => {
							this.load(this.board.id);
						});

						await dlg.load(this.board.id);

						dlg.show();
					}
				}),
				this.searchButton = searchbtn({
					listeners: {
						input: async ({text}) => {
							const cards = await this.getCards(this.board.id, text);

							this.cardGrid.store.clear();
							this.cardGrid.store.loadData(cards);
						}
					}
				}),
				btn({
					icon: "chevron_left",
					title: t("Collapse backlog"),
					handler: () => {
						this.expandedComp.hidden = true;
						this.collapsedComp.hidden = false;
						this.saveState();
					}
				})
			),
			this.cardGrid
		);

		this.collapsedComp = comp({
				cls: "vbox kanban-backlog kanban-collapsed-backlog",
				hidden: true
			},
			tbar({},
				btn({
					cls: "small",
					icon: "chevron_right",
					title: t("Expand backlog"),
					handler: () => {
						this.expandedComp.hidden = false;
						this.collapsedComp.hidden = true;
						this.saveState();
					}
				})
			),
			comp({
				style: {transform: "rotate(-90deg)", marginTop: ".8rem"},
				tagName: "h4",
				text: t("Backlog")
			})
		);

		this.items.add(this.expandedComp, this.collapsedComp);

		this.addScrollLoading();
	}

	private addScrollLoading() {
		const onScroll = () => {
			const el = this.cardGrid.el;

			if (el.scrollHeight !== 0) {
				const pixelsLeft = el.scrollHeight - el.scrollTop - el.offsetHeight;

				if (pixelsLeft < 100 && this.loaded) {
					void this.loadNext();
				}
			}
		};

		this.expandedComp.on("show", () => {
			onScroll();
		});

		this.cardGrid.el.addEventListener("scroll", onScroll, {passive: true});

		// this will fill the empty space on first load.
		this.cardGrid.store.on("load", () => {
			// use set timeout otherwise this.loading is still true
			setTimeout(() => {
				onScroll();
			})
		});
	}

	protected buildState() {
		return {
			expanded: !this.expandedComp.hidden
		}
	}

	protected restoreState(s: Record<string, any>) {
		if (s.expanded) {
			this.expandedComp.hidden = false;
			this.collapsedComp.hidden = true;
		} else {
			this.expandedComp.hidden = true;
			this.collapsedComp.hidden = false;
		}
	}

	public async load(boardId: string) {
		this.loaded = false;
		const cards = await this.getCards(boardId);
		this.board = await boardDS.single(boardId);

		this.cardGrid.store.clear();
		this.cardGrid.store.loadData(cards);

		this.cardGrid.draggableRows = this.board.permissionLevel >= AclLevel.WRITE || go.Modules.get("business", "kanban").userRights.mayManageBoards;

		this.loaded = true;
	}

	private async loadNext() {
		const offset = this.cardGrid.store.data.length;
		const limit = 10;
		this.loaded = false;

		const cards = await this.getCards(this.board.id, this.searchButton.searchField.value, offset, limit);
		if (cards.length > 0) {
			this.cardGrid.store.loadData(cards, true);
		}

		this.loaded = true;
	}

	private async getCards(boardId: string, searchText = "", offset = 0, limit = 0) {
		const cards = await client.jmap("business/kanban/Board/getBacklogCards", {
			boardId: boardId,
			search: searchText,
			offset: offset,
			limit: limit
		});

		for (const card of cards) {
			const data = await getCardEntityData(card);

			card.entityData = data.entityData;
			card.responsibleUserIds = data.responsibleUserIds;
		}

		return cards;
	}
}

export async function getCardEntityData(card: Card) {
	let entityData;
	const responsibleUserIds = [];

	switch (card.entity) {
		case "SupportTicket":
			try {
				entityData = await ticketDS.single(card.entityId);

				if (entityData.responsibleUserId) {
					responsibleUserIds.push(entityData.responsibleUserId);
				}
			} catch (e) {
				console.log(e);
			}

			break;
		case "Task":
			try {
				entityData = await taskDS.single(card.entityId);

				if (entityData.responsibleUserId) {
					responsibleUserIds.push(entityData.responsibleUserId);
				}
			} catch (e) {
				console.log(e);
			}

			break;
		case "Project3":
			try {
				entityData = await projectDS.single(card.entityId);

				for (const key in entityData.resources) {
					responsibleUserIds.push(entityData.resources[key].employeeId);
				}
			} catch (e) {
				console.log(e);
			}

			break;
	}

	return {entityData: entityData, responsibleUserIds: responsibleUserIds};
}